import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Client, User, System, CompanySystem } from '../api/entities.js';
import { supabase } from '../api/supabaseClient.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Label } from "../components/ui/label.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "../components/ui/card.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Avatar, AvatarFallback } from "../components/ui/avatar.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { Switch } from "../components/ui/switch.jsx";
import { Checkbox } from "../components/ui/checkbox.jsx";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Plus, 
  X, 
  Building2, 
  Users, 
  MapPin, 
  Phone, 
  Mail,
  Globe,
  User as UserIcon,
  Save,
  AlertCircle,
  CheckCircle2,
  Building,
  FileText,
  Cpu,
  Settings,
  Clock,
  AlertTriangle,
  Info,
  Tag,
  Calendar,
  DollarSign,
  Shield,
  Monitor,
  Key,
  Lock,
  Eye,
  EyeOff,
  Server,
  Wifi,
  Network
} from 'lucide-react';

export default function NewClient() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [client, setClient] = useState({
    corporate_name: '', // Adicionado campo razão social
    name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    status: 'active',
    cnpj: '',
    technical_contacts: [],
    systems: [],
    sla_settings: {
      low_priority: 72,
      medium_priority: 24,
      high_priority: 8,
      critical_priority: 2,
      business_hours_only: false,
      weekend_support: false
    },
    billing_info: {
      contract_type: 'monthly',
      monthly_hours: 0,
      hourly_rate: 0,
      support_plan: 'basic',
      auto_renew: true
    },
    preferences: {
      notifications_email: true,
      notifications_sms: false,
      reports_frequency: 'monthly',
      priority_escalation: true
    },
    remote_access: {
      teamviewer: { id: '', password: '', notes: '', active: false },
      anydesk: { id: '', password: '', notes: '', active: false },
      terminal_services: { host: '', port: '', username: '', password: '', notes: '', active: false },
      chrome_remote: { email: '', notes: '', active: false },
      vpn: { host: '', username: '', password: '', notes: '', active: false },
      other_tools: []
    },
    notes: '',
    tags: [],
    metadata: {}
  });

  const [availableSystems, setAvailableSystems] = useState([]);
  const [isLoadingSystems, setIsLoadingSystems] = useState(false);

  // Carregar sistemas quando o componente monta
  useEffect(() => {
    loadSystems();
  }, []);

  // Carregar sistemas disponíveis
  const loadSystems = async () => {
    setIsLoadingSystems(true);
    try {
      console.log('🔄 Carregando sistemas...');
      const systems = await System.findAll();
      console.log('📊 Sistemas carregados:', systems);
      setAvailableSystems(systems || []);
    } catch (error) {
      console.error('❌ Erro ao carregar sistemas:', error);
      setAvailableSystems([]);
    }
    setIsLoadingSystems(false);
  };

  const addContact = () => {
    setClient({
      ...client,
      technical_contacts: [
        ...client.technical_contacts,
        { name: '', email: '', phone: '', role: '' }
      ]
    });
  };

  const removeContact = (index) => {
    const newContacts = [...client.technical_contacts];
    newContacts.splice(index, 1);
    setClient({ ...client, technical_contacts: newContacts });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...client.technical_contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setClient({ ...client, technical_contacts: newContacts });
  };

  // Gerenciar sistemas
  const toggleSystem = (systemId) => {
    const currentSystems = client.systems || [];
    const isSelected = currentSystems.some(s => s.system_id === systemId);
    
    if (isSelected) {
      const newSystems = currentSystems.filter(s => s.system_id !== systemId);
      setClient({ ...client, systems: newSystems });
    } else {
      const system = availableSystems.find(s => s.id === systemId);
      const newSystems = [...currentSystems, {
        system_id: systemId,
        system_name: system?.name || 'Sistema',
        status: 'active',
        installation_date: new Date().toISOString().split('T')[0],
        license_type: 'standard',
        max_users: 10,
        notes: ''
      }];
      setClient({ ...client, systems: newSystems });
    }
  };

  const updateSystemConfig = (systemId, field, value) => {
    const newSystems = client.systems.map(s => 
      s.system_id === systemId ? { ...s, [field]: value } : s
    );
    setClient({ ...client, systems: newSystems });
  };

  // Gerenciar tags
  const addTag = (tagText) => {
    if (tagText.trim() && !client.tags.includes(tagText.trim())) {
      setClient({ ...client, tags: [...client.tags, tagText.trim()] });
    }
  };

  const removeTag = (tagToRemove) => {
    setClient({ ...client, tags: client.tags.filter(tag => tag !== tagToRemove) });
  };

  // Atualizar configurações de SLA
  const updateSLASetting = (field, value) => {
    setClient({
      ...client,
      sla_settings: { ...client.sla_settings, [field]: value }
    });
  };

  // Atualizar informações de cobrança
  const updateBillingInfo = (field, value) => {
    setClient({
      ...client,
      billing_info: { ...client.billing_info, [field]: value }
    });
  };

  // Atualizar preferências
  const updatePreference = (field, value) => {
    setClient({
      ...client,
      preferences: { ...client.preferences, [field]: value }
    });
  };

  // Gerenciar dados de acesso remoto
  const updateRemoteAccess = (tool, field, value) => {
    setClient({
      ...client,
      remote_access: {
        ...client.remote_access,
        [tool]: { ...client.remote_access[tool], [field]: value }
      }
    });
  };

  const addOtherTool = () => {
    setClient({
      ...client,
      remote_access: {
        ...client.remote_access,
        other_tools: [
          ...client.remote_access.other_tools,
          { name: '', credentials: '', notes: '', active: false }
        ]
      }
    });
  };

  const removeOtherTool = (index) => {
    const newTools = [...client.remote_access.other_tools];
    newTools.splice(index, 1);
    setClient({
      ...client,
      remote_access: { ...client.remote_access, other_tools: newTools }
    });
  };

  const updateOtherTool = (index, field, value) => {
    const newTools = [...client.remote_access.other_tools];
    newTools[index] = { ...newTools[index], [field]: value };
    setClient({
      ...client,
      remote_access: { ...client.remote_access, other_tools: newTools }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Verificar se o usuário está autenticado
      const currentUser = await User.getCurrentUser();
      if (!currentUser) {
        throw new Error('Usuário não autenticado. Faça login novamente.');
      }

      // Preparar dados para compatibilidade com a tabela existente
      const clientData = {
        corporate_name: client.corporate_name, // Salvar razão social como coluna principal
        name: client.name,
        company_name: client.company_name,
        cnpj: client.cnpj, // Salvar CNPJ como coluna principal
        email: client.email,
        phone: client.phone,
        address: client.address,
        status: client.status,
        remote_access: client.remote_access, // Salvar dados de acesso remoto
        metadata: {
          technical_contacts: client.technical_contacts,
          systems: client.systems,
          sla_settings: client.sla_settings,
          billing_info: client.billing_info,
          preferences: client.preferences,
          notes: client.notes,
          tags: client.tags,
          ...client.metadata
        }
      };
      
      console.log('Criando cliente com dados:', clientData);
      await Client.create(clientData);
      navigate(createPageUrl('Clients'));
    } catch (error) {
      console.error('Error creating client:', error);
      
      // Verificar tipo de erro específico
      if (error.message?.includes('row-level security') || error.message?.includes('unauthorized')) {
        alert('Erro de permissão: Você não tem autorização para criar clientes. Verifique suas credenciais e tente novamente.');
      } else if (error.message?.includes('cnpj')) {
        alert('Erro: A tabela não suporta o campo CNPJ. Execute o script de migração no Supabase.');
      } else {
        alert(`Erro ao criar cliente: ${error.message || 'Erro desconhecido'}`);
      }
    }
    setIsLoading(false);
  };

  const getClientInitials = () => {
    const name = client.company_name || client.name || 'Novo Cliente';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-6"
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Clients'))}
          className="h-10 w-10"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        
        <div className="flex items-center gap-6 flex-1">
          <Avatar className="h-16 w-16 border-4 border-background shadow-lg">
            <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-white font-bold text-xl">
              {getClientInitials()}
            </AvatarFallback>
          </Avatar>
          
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              Novo Cliente
            </h1>
            <p className="text-muted-foreground mt-2 text-lg">
              Cadastre um novo cliente no sistema
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(createPageUrl('Clients'))}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading}
            className="gap-2 shadow-lg"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Salvar Cliente
              </>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="border-0 shadow-xl">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <CardHeader className="pb-0">
              <TabsList className="grid w-full grid-cols-5 bg-muted/50 p-1 h-12">
                <TabsTrigger value="basic" className="flex items-center gap-2 text-sm font-medium">
                  <Building2 className="h-4 w-4" />
                  Informações Básicas
                </TabsTrigger>
                <TabsTrigger value="contacts" className="flex items-center gap-2 text-sm font-medium">
                  <Users className="h-4 w-4" />
                  Contatos Técnicos
                </TabsTrigger>
                <TabsTrigger value="systems" className="flex items-center gap-2 text-sm font-medium">
                  <Cpu className="h-4 w-4" />
                  Sistemas ({client.systems?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="remote" className="flex items-center gap-2 text-sm font-medium">
                  <Monitor className="h-4 w-4" />
                  Acesso Remoto
                </TabsTrigger>
                <TabsTrigger value="additional" className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-4 w-4" />
                  Informações Adicionais
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="pt-6">
              <form onSubmit={handleSubmit}>
                {/* Aba: Informações Básicas */}
                <TabsContent value="basic" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Building className="h-5 w-5 text-primary" />
                      Dados da Empresa
                    </h3>
                    
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="corporate_name" className="text-sm font-medium">
                          Razão Social *
                        </Label>
                        <Input
                          id="corporate_name"
                          placeholder="Digite a razão social"
                          value={client.corporate_name}
                          onChange={(e) => setClient({ ...client, corporate_name: e.target.value })}
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company_name" className="text-sm font-medium">
                          Nome Fantasia
                        </Label>
                        <Input
                          id="company_name"
                          placeholder="Digite o nome fantasia"
                          value={client.company_name}
                          onChange={(e) => setClient({ ...client, company_name: e.target.value })}
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium">
                          Nome do Contato Principal *
                        </Label>
                        <Input
                          id="name"
                          placeholder="Digite o nome do contato"
                          value={client.name}
                          onChange={(e) => setClient({ ...client, name: e.target.value })}
                          required
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cnpj" className="text-sm font-medium">
                          CNPJ
                        </Label>
                        <Input
                          id="cnpj"
                          placeholder="00.000.000/0000-00"
                          value={client.cnpj}
                          onChange={(e) => setClient({ ...client, cnpj: e.target.value })}
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status" className="text-sm font-medium">
                          Status
                        </Label>
                        <Select value={client.status} onValueChange={(value) => setClient({ ...client, status: value })}>
                          <SelectTrigger className="h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                Ativo
                              </div>
                            </SelectItem>
                            <SelectItem value="inactive">
                              <div className="flex items-center gap-2">
                                <X className="h-4 w-4 text-red-600" />
                                Inativo
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      Informações de Contato
                    </h3>
                    
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium">
                          E-mail *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="exemplo@empresa.com"
                          value={client.email}
                          onChange={(e) => setClient({ ...client, email: e.target.value })}
                          required
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium">
                          Telefone
                        </Label>
                        <Input
                          id="phone"
                          placeholder="(11) 99999-9999"
                          value={client.phone}
                          onChange={(e) => setClient({ ...client, phone: e.target.value })}
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="address" className="text-sm font-medium">
                          Endereço
                        </Label>
                        <Textarea
                          id="address"
                          placeholder="Digite o endereço completo"
                          value={client.address}
                          onChange={(e) => setClient({ ...client, address: e.target.value })}
                          rows={3}
                          className="resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Aba: Contatos Técnicos */}
                <TabsContent value="contacts" className="space-y-6 mt-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        Contatos Técnicos
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Adicione os contatos técnicos responsáveis pelo suporte
                      </p>
                    </div>
                    <Button 
                      type="button" 
                      onClick={addContact}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar Contato
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {client.technical_contacts.length === 0 ? (
                      <Card className="py-12 border-dashed border-2">
                        <CardContent className="text-center">
                          <UserIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <h4 className="text-lg font-medium mb-2">Nenhum contato adicionado</h4>
                          <p className="text-muted-foreground mb-4">
                            Adicione contatos técnicos para facilitar o suporte
                          </p>
                          <Button onClick={addContact} variant="outline">
                            <Plus className="h-4 w-4 mr-2" />
                            Adicionar Primeiro Contato
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      client.technical_contacts.map((contact, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                        >
                          <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-3">
                              <CardTitle className="text-base flex items-center gap-2">
                                <UserIcon className="h-4 w-4" />
                                Contato {index + 1}
                              </CardTitle>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeContact(index)}
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Nome</Label>
                                <Input
                                  placeholder="Nome do contato"
                                  value={contact.name}
                                  onChange={(e) => updateContact(index, 'name', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Cargo</Label>
                                <Input
                                  placeholder="Cargo ou função"
                                  value={contact.role}
                                  onChange={(e) => updateContact(index, 'role', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">E-mail</Label>
                                <Input
                                  type="email"
                                  placeholder="email@empresa.com"
                                  value={contact.email}
                                  onChange={(e) => updateContact(index, 'email', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Telefone</Label>
                                <Input
                                  placeholder="(11) 99999-9999"
                                  value={contact.phone}
                                  onChange={(e) => updateContact(index, 'phone', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))
                    )}
                  </div>
                </TabsContent>

                {/* Aba: Sistemas */}
                <TabsContent value="systems" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Cpu className="h-5 w-5 text-primary" />
                      Sistemas Utilizados
                    </h3>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Selecione os sistemas que o cliente utiliza</CardTitle>
                        <CardDescription>
                          Marque os sistemas que serão utilizados pelo cliente para configurar o suporte adequado
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {isLoadingSystems ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            <span className="ml-3">Carregando sistemas...</span>
                          </div>
                        ) : availableSystems.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Cpu className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Nenhum sistema cadastrado</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid gap-3 md:grid-cols-2">
                              {availableSystems.map((system) => {
                                const isSelected = client.systems.some(s => s.system_id === system.id);
                                return (
                                  <div key={system.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                                    <Checkbox
                                      id={`system-${system.id}`}
                                      checked={isSelected}
                                      onCheckedChange={() => toggleSystem(system.id)}
                                    />
                                    <div className="flex-1">
                                      <Label htmlFor={`system-${system.id}`} className="font-medium cursor-pointer">
                                        {system.name}
                                      </Label>
                                      {system.description && (
                                        <p className="text-sm text-muted-foreground">{system.description}</p>
                                      )}
                                    </div>
                                    <Badge variant="outline" className="text-xs">
                                      {system.type || 'Sistema'}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Configurações dos sistemas selecionados */}
                            {client.systems.length > 0 && (
                              <div className="mt-6">
                                <h4 className="font-medium mb-3">Configurações dos Sistemas Selecionados</h4>
                                <div className="space-y-4">
                                  {client.systems.map((systemConfig) => (
                                    <Card key={systemConfig.system_id} className="border-l-4 border-l-primary">
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-base">{systemConfig.system_name}</CardTitle>
                                      </CardHeader>
                                      <CardContent className="grid gap-4 md:grid-cols-3">
                                        <div className="space-y-2">
                                          <Label className="text-sm">Data de Instalação</Label>
                                          <Input
                                            type="date"
                                            value={systemConfig.installation_date}
                                            onChange={(e) => updateSystemConfig(systemConfig.system_id, 'installation_date', e.target.value)}
                                            className="h-9"
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          <Label className="text-sm">Tipo de Licença</Label>
                                          <Select 
                                            value={systemConfig.license_type} 
                                            onValueChange={(value) => updateSystemConfig(systemConfig.system_id, 'license_type', value)}
                                          >
                                            <SelectTrigger className="h-9">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="standard">Padrão</SelectItem>
                                              <SelectItem value="premium">Premium</SelectItem>
                                              <SelectItem value="enterprise">Enterprise</SelectItem>
                                              <SelectItem value="trial">Trial</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-2">
                                          <Label className="text-sm">Máximo de Usuários</Label>
                                          <Input
                                            type="number"
                                            min="1"
                                            value={systemConfig.max_users}
                                            onChange={(e) => updateSystemConfig(systemConfig.system_id, 'max_users', parseInt(e.target.value) || 1)}
                                            className="h-9"
                                          />
                                        </div>
                                        <div className="space-y-2 md:col-span-3">
                                          <Label className="text-sm">Observações</Label>
                                          <Textarea
                                            placeholder="Observações específicas sobre este sistema..."
                                            value={systemConfig.notes}
                                            onChange={(e) => updateSystemConfig(systemConfig.system_id, 'notes', e.target.value)}
                                            rows={2}
                                            className="resize-none"
                                          />
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Aba: Acesso Remoto */}
                <TabsContent value="remote" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Monitor className="h-5 w-5 text-primary" />
                      Dados de Acesso Remoto
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Configure os dados de acesso remoto para suporte técnico
                    </p>

                    <div className="space-y-6">
                      {/* TeamViewer */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Monitor className="h-4 w-4 text-blue-600" />
                            TeamViewer
                            <Switch
                              checked={client.remote_access.teamviewer.active}
                              onCheckedChange={(checked) => updateRemoteAccess('teamviewer', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.teamviewer.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">ID TeamViewer</Label>
                                <Input
                                  placeholder="123 456 789"
                                  value={client.remote_access.teamviewer.id}
                                  onChange={(e) => updateRemoteAccess('teamviewer', 'id', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="password"
                                  placeholder="Senha de acesso"
                                  value={client.remote_access.teamviewer.password}
                                  onChange={(e) => updateRemoteAccess('teamviewer', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Observações</Label>
                              <Textarea
                                placeholder="Observações sobre o acesso TeamViewer..."
                                value={client.remote_access.teamviewer.notes}
                                onChange={(e) => updateRemoteAccess('teamviewer', 'notes', e.target.value)}
                                rows={2}
                                className="resize-none"
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* AnyDesk */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Server className="h-4 w-4 text-red-600" />
                            AnyDesk
                            <Switch
                              checked={client.remote_access.anydesk.active}
                              onCheckedChange={(checked) => updateRemoteAccess('anydesk', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.anydesk.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">ID AnyDesk</Label>
                                <Input
                                  placeholder="123 456 789"
                                  value={client.remote_access.anydesk.id}
                                  onChange={(e) => updateRemoteAccess('anydesk', 'id', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="password"
                                  placeholder="Senha de acesso"
                                  value={client.remote_access.anydesk.password}
                                  onChange={(e) => updateRemoteAccess('anydesk', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Observações</Label>
                              <Textarea
                                placeholder="Observações sobre o acesso AnyDesk..."
                                value={client.remote_access.anydesk.notes}
                                onChange={(e) => updateRemoteAccess('anydesk', 'notes', e.target.value)}
                                rows={2}
                                className="resize-none"
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* Terminal Services / RDP */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Network className="h-4 w-4 text-green-600" />
                            Terminal Services / RDP
                            <Switch
                              checked={client.remote_access.terminal_services.active}
                              onCheckedChange={(checked) => updateRemoteAccess('terminal_services', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.terminal_services.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-3">
                              <div className="space-y-2">
                                <Label className="text-sm">Host/IP</Label>
                                <Input
                                  placeholder="192.168.1.100"
                                  value={client.remote_access.terminal_services.host}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'host', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Porta</Label>
                                <Input
                                  placeholder="3389"
                                  value={client.remote_access.terminal_services.port}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'port', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Usuário</Label>
                                <Input
                                  placeholder="usuário"
                                  value={client.remote_access.terminal_services.username}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'username', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="password"
                                  placeholder="Senha de acesso"
                                  value={client.remote_access.terminal_services.password}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Observações</Label>
                                <Textarea
                                  placeholder="Observações sobre o acesso RDP..."
                                  value={client.remote_access.terminal_services.notes}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'notes', e.target.value)}
                                  rows={2}
                                  className="resize-none"
                                />
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* Chrome Remote Desktop */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Globe className="h-4 w-4 text-purple-600" />
                            Chrome Remote Desktop
                            <Switch
                              checked={client.remote_access.chrome_remote.active}
                              onCheckedChange={(checked) => updateRemoteAccess('chrome_remote', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.chrome_remote.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">E-mail da Conta Google</Label>
                                <Input
                                  type="email"
                                  placeholder="exemplo@gmail.com"
                                  value={client.remote_access.chrome_remote.email}
                                  onChange={(e) => updateRemoteAccess('chrome_remote', 'email', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Observações</Label>
                                <Textarea
                                  placeholder="Observações sobre o acesso Chrome Remote..."
                                  value={client.remote_access.chrome_remote.notes}
                                  onChange={(e) => updateRemoteAccess('chrome_remote', 'notes', e.target.value)}
                                  rows={2}
                                  className="resize-none"
                                />
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* VPN */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Shield className="h-4 w-4 text-orange-600" />
                            VPN
                            <Switch
                              checked={client.remote_access.vpn.active}
                              onCheckedChange={(checked) => updateRemoteAccess('vpn', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.vpn.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-3">
                              <div className="space-y-2">
                                <Label className="text-sm">Servidor VPN</Label>
                                <Input
                                  placeholder="vpn.empresa.com"
                                  value={client.remote_access.vpn.host}
                                  onChange={(e) => updateRemoteAccess('vpn', 'host', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Usuário</Label>
                                <Input
                                  placeholder="usuário VPN"
                                  value={client.remote_access.vpn.username}
                                  onChange={(e) => updateRemoteAccess('vpn', 'username', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="password"
                                  placeholder="Senha VPN"
                                  value={client.remote_access.vpn.password}
                                  onChange={(e) => updateRemoteAccess('vpn', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Observações</Label>
                              <Textarea
                                placeholder="Observações sobre o acesso VPN..."
                                value={client.remote_access.vpn.notes}
                                onChange={(e) => updateRemoteAccess('vpn', 'notes', e.target.value)}
                                rows={2}
                                className="resize-none"
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* Outras Ferramentas */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Key className="h-4 w-4 text-gray-600" />
                            Outras Ferramentas
                          </CardTitle>
                          <CardDescription>
                            Adicione outras ferramentas de acesso remoto não listadas acima
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {client.remote_access.other_tools.map((tool, index) => (
                              <Card key={index} className="border-l-4 border-l-gray-400">
                                <CardHeader className="pb-3">
                                  <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm">Ferramenta {index + 1}</CardTitle>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removeOtherTool(index)}
                                      className="h-8 w-8 text-red-500 hover:text-red-700"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  <div className="grid gap-4 md:grid-cols-3">
                                    <div className="space-y-2">
                                      <Label className="text-sm">Nome da Ferramenta</Label>
                                      <Input
                                        placeholder="Ex: LogMeIn, UltraVNC..."
                                        value={tool.name}
                                        onChange={(e) => updateOtherTool(index, 'name', e.target.value)}
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label className="text-sm">Credenciais</Label>
                                      <Input
                                        placeholder="ID, usuário, etc."
                                        value={tool.credentials}
                                        onChange={(e) => updateOtherTool(index, 'credentials', e.target.value)}
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label className="text-sm">Status</Label>
                                      <Switch
                                        checked={tool.active}
                                        onCheckedChange={(checked) => updateOtherTool(index, 'active', checked)}
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-sm">Observações</Label>
                                    <Textarea
                                      placeholder="Observações sobre esta ferramenta..."
                                      value={tool.notes}
                                      onChange={(e) => updateOtherTool(index, 'notes', e.target.value)}
                                      rows={2}
                                      className="resize-none"
                                    />
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                            
                            <Button
                              type="button"
                              variant="outline"
                              onClick={addOtherTool}
                              className="w-full gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Adicionar Outra Ferramenta
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                {/* Aba: Informações Adicionais */}
                <TabsContent value="additional" className="space-y-6 mt-0">

                  {/* Configurações de SLA */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-primary" />
                      Configurações de SLA
                    </h3>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Tempos de Resposta (em horas)</CardTitle>
                        <CardDescription>
                          Configure os tempos máximos de resposta para cada prioridade de chamado
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-sm flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-green-500"></div>
                              Prioridade Baixa
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                value={client.sla_settings.low_priority}
                                onChange={(e) => updateSLASetting('low_priority', parseInt(e.target.value) || 1)}
                                className="h-9"
                              />
                              <span className="text-sm text-muted-foreground">horas</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                              Prioridade Média
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                value={client.sla_settings.medium_priority}
                                onChange={(e) => updateSLASetting('medium_priority', parseInt(e.target.value) || 1)}
                                className="h-9"
                              />
                              <span className="text-sm text-muted-foreground">horas</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                              Prioridade Alta
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                value={client.sla_settings.high_priority}
                                onChange={(e) => updateSLASetting('high_priority', parseInt(e.target.value) || 1)}
                                className="h-9"
                              />
                              <span className="text-sm text-muted-foreground">horas</span>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-sm flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-red-500"></div>
                              Prioridade Crítica
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                value={client.sla_settings.critical_priority}
                                onChange={(e) => updateSLASetting('critical_priority', parseInt(e.target.value) || 1)}
                                className="h-9"
                              />
                              <span className="text-sm text-muted-foreground">horas</span>
                            </div>
                          </div>
                        </div>

                        <Separator />

                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <Label className="text-sm font-medium">Suporte apenas em horário comercial</Label>
                              <p className="text-xs text-muted-foreground">
                                SLA conta apenas durante horário comercial (8h às 18h)
                              </p>
                            </div>
                            <Switch
                              checked={client.sla_settings.business_hours_only}
                              onCheckedChange={(checked) => updateSLASetting('business_hours_only', checked)}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <Label className="text-sm font-medium">Suporte em finais de semana</Label>
                              <p className="text-xs text-muted-foreground">
                                Cliente possui suporte em sábados e domingos
                              </p>
                            </div>
                            <Switch
                              checked={client.sla_settings.weekend_support}
                              onCheckedChange={(checked) => updateSLASetting('weekend_support', checked)}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Informações de Cobrança */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-primary" />
                      Informações de Cobrança
                    </h3>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Configurações de Contrato e Cobrança</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label className="text-sm">Tipo de Contrato</Label>
                            <Select 
                              value={client.billing_info.contract_type} 
                              onValueChange={(value) => updateBillingInfo('contract_type', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Mensal</SelectItem>
                                <SelectItem value="quarterly">Trimestral</SelectItem>
                                <SelectItem value="yearly">Anual</SelectItem>
                                <SelectItem value="hourly">Por Hora</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Plano de Suporte</Label>
                            <Select 
                              value={client.billing_info.support_plan} 
                              onValueChange={(value) => updateBillingInfo('support_plan', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="basic">Básico</SelectItem>
                                <SelectItem value="standard">Padrão</SelectItem>
                                <SelectItem value="premium">Premium</SelectItem>
                                <SelectItem value="enterprise">Enterprise</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Horas Mensais Incluídas</Label>
                            <Input
                              type="number"
                              min="0"
                              value={client.billing_info.monthly_hours}
                              onChange={(e) => updateBillingInfo('monthly_hours', parseInt(e.target.value) || 0)}
                              placeholder="0"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Valor por Hora Extra (R$)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={client.billing_info.hourly_rate}
                              onChange={(e) => updateBillingInfo('hourly_rate', parseFloat(e.target.value) || 0)}
                              placeholder="0.00"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                          <div className="space-y-1">
                            <Label className="text-sm font-medium">Renovação Automática</Label>
                            <p className="text-xs text-muted-foreground">
                              Renovar contrato automaticamente no vencimento
                            </p>
                          </div>
                          <Switch
                            checked={client.billing_info.auto_renew}
                            onCheckedChange={(checked) => updateBillingInfo('auto_renew', checked)}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Preferências de Comunicação */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Settings className="h-5 w-5 text-primary" />
                      Preferências de Comunicação
                    </h3>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Configurações de Notificações e Relatórios</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <Label className="text-sm font-medium">Notificações por E-mail</Label>
                              <p className="text-xs text-muted-foreground">
                                Receber notificações sobre chamados por e-mail
                              </p>
                            </div>
                            <Switch
                              checked={client.preferences.notifications_email}
                              onCheckedChange={(checked) => updatePreference('notifications_email', checked)}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <Label className="text-sm font-medium">Notificações por SMS</Label>
                              <p className="text-xs text-muted-foreground">
                                Receber notificações urgentes por SMS
                              </p>
                            </div>
                            <Switch
                              checked={client.preferences.notifications_sms}
                              onCheckedChange={(checked) => updatePreference('notifications_sms', checked)}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <Label className="text-sm font-medium">Escalação Automática</Label>
                              <p className="text-xs text-muted-foreground">
                                Escalar chamados automaticamente quando SLA está próximo do vencimento
                              </p>
                            </div>
                            <Switch
                              checked={client.preferences.priority_escalation}
                              onCheckedChange={(checked) => updatePreference('priority_escalation', checked)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm">Frequência de Relatórios</Label>
                            <Select 
                              value={client.preferences.reports_frequency} 
                              onValueChange={(value) => updatePreference('reports_frequency', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="weekly">Semanal</SelectItem>
                                <SelectItem value="monthly">Mensal</SelectItem>
                                <SelectItem value="quarterly">Trimestral</SelectItem>
                                <SelectItem value="never">Nunca</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Tags e Observações */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Tag className="h-5 w-5 text-primary" />
                      Tags e Observações
                    </h3>
                    
                    <div className="grid gap-4 md:grid-cols-2">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Tags</CardTitle>
                          <CardDescription>
                            Adicione tags para categorizar o cliente
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Digite uma tag..."
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addTag(e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              className="h-9"
                            />
                            <Button 
                              type="button" 
                              size="sm"
                              onClick={(e) => {
                                const input = e.target.parentElement.querySelector('input');
                                addTag(input.value);
                                input.value = '';
                              }}
                            >
                              Adicionar
                            </Button>
                          </div>
                          
                          {client.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {client.tags.map((tag, index) => (
                                <Badge key={index} variant="secondary" className="gap-1">
                                  {tag}
                                  <X 
                                    className="h-3 w-3 cursor-pointer hover:text-red-500" 
                                    onClick={() => removeTag(tag)}
                                  />
                                </Badge>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Observações Gerais</CardTitle>
                          <CardDescription>
                            Informações adicionais sobre o cliente
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Textarea
                            placeholder="Digite observações importantes sobre o cliente..."
                            value={client.notes}
                            onChange={(e) => setClient({ ...client, notes: e.target.value })}
                            rows={6}
                            className="resize-none"
                          />
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>
              </form>
            </CardContent>
          </Tabs>
        </Card>
      </motion.div>

      {/* Progress Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="fixed bottom-6 right-6"
      >
        <Card className="p-4 shadow-lg border-0">
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-2">
              {activeTab === 'basic' && <Building2 className="h-4 w-4 text-primary" />}
              {activeTab === 'contacts' && <Users className="h-4 w-4 text-primary" />}
              {activeTab === 'additional' && <FileText className="h-4 w-4 text-primary" />}
              <span className="font-medium">
                Etapa {activeTab === 'basic' ? '1' : activeTab === 'contacts' ? '2' : activeTab === 'systems' ? '3' : activeTab === 'remote' ? '4' : '5'} de 5
              </span>
            </div>
            <Badge variant="outline">
              {activeTab === 'basic' && 'Informações Básicas'}
              {activeTab === 'contacts' && 'Contatos'}
              {activeTab === 'systems' && 'Sistemas'}
              {activeTab === 'remote' && 'Acesso Remoto'}
              {activeTab === 'additional' && 'Adicionais'}
            </Badge>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}